import { useState, useEffect } from 'react';
import { useRootDataContext } from '@context/RootData/RootDataContext';
import { FILTER_TYPES } from '@config';

const useURLParamsFacets = (url = window.location.search) => {
    const rootData = useRootDataContext();

    /**
     * The initial state will get the URLSearchParams from the current
     * location and turn it into a facets object.
     */
    const [paramsFacets, setParamsFacets] = useState(() => {
        const searchParams = new URLSearchParams(url);

        const uniqueKeys = new Set();
        for (const key of searchParams.keys()) {
            uniqueKeys.add(key);
        }

        const facets = {};
        for (const key of uniqueKeys) {
            const values = searchParams
                .getAll(key)
                /**
                 * Facet values should all be in lowercase.
                 * The brands-models Web-Component receives capitalized data from the API.
                 * So, to be sure, we need to transform the strings to lowercase.
                 */
                .map(value => value.toLowerCase());

            const isRangeType = FILTER_TYPES[key] === 'range';

            if (isRangeType) {
                facets[key] = {
                    range: values
                };
            } else {
                facets[key] = {
                    values: values
                };
            }
        }

        return facets;
    });

    /**
     * Converts a factes object into a URLSearchParams instance.
     *
     * @param {object} facets A facets object.
     * @returns {URLSearchParams}
     */
    const facetsToURLParams = facets => {
        const searchParams = new URLSearchParams();

        for (const [key, data] of Object.entries(facets)) {
            const values =
                FILTER_TYPES[key] === 'range' ? data.range : data.values;

            for (const value of values) {
                searchParams.append(key, value);
            }
        }

        return searchParams;
    };

    /**
     * Whenever the facets have been update, the URL should reflect the changes.
     * Here we convert the facets into an URLSearchParams object and update set the new URL.
     */
    useEffect(() => {
        const urlParams = facetsToURLParams(paramsFacets);
        const url = new URL(rootData.slug, window.location.origin);
        url.search = urlParams;
        window.history.pushState(paramsFacets, null, url);
    }, [paramsFacets, rootData]);

    return [paramsFacets, setParamsFacets];
};

export default useURLParamsFacets;
